import { useState, useEffect } from 'react';
import { documentsApi } from '../lib/api';
import api from '../lib/api';
import { Button } from './ui/button';
import { Upload, Warning, File } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { ExtractionVerificationDialog } from './DocumentExtractor';
import { IdDocumentVerificationDialog } from './IdDocumentExtractor';
import {
    DocumentRow,
    RequiredDocumentCard,
    UploadDocumentDialog,
    PreviewDocumentDialog,
    VersionsDialog,
    DeleteDocumentDialog
} from './document';

export default function DocumentManager({
    contractId,
    nodeId,
    requiredDocs = [],
    optionalDocs = [],
    documents = [],
    onRefresh,
    nodeStatus = 'in_corso',
    canValidate = false
}) {
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
    const [selectedDocType, setSelectedDocType] = useState(null);
    const [replaceDocId, setReplaceDocId] = useState(null);
    const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
    const [previewDoc, setPreviewDoc] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [versionsDialogOpen, setVersionsDialogOpen] = useState(false);
    const [selectedDocVersions, setSelectedDocVersions] = useState([]);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [docToDelete, setDocToDelete] = useState(null);

    // Stati estrazione AI
    const [extractionDialogOpen, setExtractionDialogOpen] = useState(false);
    const [currentExtraction, setCurrentExtraction] = useState(null);
    const [documentForExtraction, setDocumentForExtraction] = useState(null);

    // Raggruppa documenti per tipo
    const docsByType = {};
    documents.forEach(doc => {
        if (!docsByType[doc.document_type]) {
            docsByType[doc.document_type] = [];
        }
        docsByType[doc.document_type].push(doc);
    });

    const knownTypes = [...requiredDocs, ...optionalDocs].map(d => d.code);
    const otherDocs = documents.filter(d => !knownTypes.includes(d.document_type) && d.is_latest);

    const handleUploadClick = (docReq) => {
        setSelectedDocType(docReq);
        setReplaceDocId(null);
        setUploadDialogOpen(true);
    };

    const handleReplaceClick = (doc) => {
        setSelectedDocType({ code: doc.document_type, label: doc.document_type });
        setReplaceDocId(doc.id);
        setUploadDialogOpen(true);
    };

    const handleUpload = async (file) => {
        if (!file) return;

        setUploading(true);
        setUploadProgress(10);

        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('contract_id', contractId);
            formData.append('contract_node_id', nodeId || '');
            formData.append('document_type', selectedDocType?.code || 'ALTRO');
            formData.append('category', 'step_document');
            if (replaceDocId) {
                formData.append('replace_document_id', replaceDocId);
            }

            setUploadProgress(30);

            await documentsApi.upload(formData);

            setUploadProgress(100);
            toast.success(replaceDocId ? 'Nuova versione caricata' : 'Documento caricato');

            setUploadDialogOpen(false);
            setSelectedDocType(null);
            setReplaceDocId(null);

            if (onRefresh) {
                onRefresh();
            }
        } catch (error) {
            console.error('Errore upload:', error);
            toast.error('Errore nel caricamento del documento');
        } finally {
            setUploading(false);
            setUploadProgress(0);
        }
    };

    const handleDownload = async (doc) => {
        try {
            const response = await documentsApi.download(doc.id);
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', doc.original_filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Errore download:', error);
            toast.error('Errore nel download');
        }
    };

    const handlePreview = async (doc) => {
        setPreviewDoc(doc);

        const contentType = doc.content_type || '';
        if (contentType.startsWith('image/') || contentType === 'application/pdf') {
            try {
                const response = await documentsApi.download(doc.id);
                const blob = new Blob([response.data], { type: contentType });
                const url = window.URL.createObjectURL(blob);
                setPreviewUrl(url);
            } catch (error) {
                console.error('Errore anteprima:', error);
                setPreviewUrl(null);
            }
        } else {
            setPreviewUrl(null);
        }

        setPreviewDialogOpen(true);
    };

    const handleViewVersions = async (doc) => {
        try {
            const response = await documentsApi.getVersions(doc.id);
            setSelectedDocVersions(response.data);
            setVersionsDialogOpen(true);
        } catch (error) {
            console.error('Errore caricamento versioni:', error);
            toast.error('Errore nel caricamento delle versioni');
        }
    };

    const handleValidate = async (doc, status) => {
        try {
            await documentsApi.validate(doc.id, { status });
            toast.success(status === 'approvato' ? 'Documento approvato' : 'Documento rifiutato');
            if (onRefresh) {
                onRefresh();
            }
        } catch (error) {
            console.error('Errore validazione:', error);
            toast.error('Errore nella validazione');
        }
    };

    const handleDeleteClick = (doc) => {
        setDocToDelete(doc);
        setDeleteDialogOpen(true);
    };

    const handleDelete = async () => {
        if (!docToDelete) return;

        try {
            await documentsApi.delete(docToDelete.id);
            toast.success('Documento eliminato');
            setDeleteDialogOpen(false);
            setDocToDelete(null);
            if (onRefresh) {
                onRefresh();
            }
        } catch (error) {
            console.error('Errore eliminazione:', error);
            toast.error('Errore nell\'eliminazione');
        }
    };

    // ============ Estrazione AI ============

    const handleExtract = async (doc) => {
        try {
            toast.info('Avvio estrazione dati...', { duration: 2000 });

            const response = await api.post(`/documents/${doc.id}/extract`);
            const extractionId = response.data.extraction_id;

            toast.info('Estrazione in corso...', { duration: 3000 });

            await pollExtractionStatus(extractionId, doc);
        } catch (error) {
            console.error('Errore estrazione:', error);
            toast.error(error.response?.data?.detail || 'Errore durante l\'estrazione');
        }
    };

    const pollExtractionStatus = async (extractionId, doc, maxAttempts = 30) => {
        for (let i = 0; i < maxAttempts; i++) {
            await new Promise(resolve => setTimeout(resolve, 2000));

            try {
                const response = await api.get(`/extractions/${extractionId}`);
                const extractionData = response.data;

                if (extractionData.status === 'estratto') {
                    toast.success('Estrazione completata! Verifica i dati.');
                    setCurrentExtraction(extractionData);
                    setDocumentForExtraction(doc);
                    setExtractionDialogOpen(true);
                    if (onRefresh) onRefresh();
                    return;
                }

                if (extractionData.status === 'errore') {
                    toast.error('Errore: ' + (extractionData.error || 'Errore sconosciuto'));
                    if (onRefresh) onRefresh();
                    return;
                }

                if (extractionData.status === 'validato') {
                    if (onRefresh) onRefresh();
                    return;
                }
            } catch (error) {
                console.error('Errore polling:', error);
            }
        }

        toast.error('Timeout durante l\'estrazione');
    };

    const handleVerifyExtraction = async (doc) => {
        try {
            const response = await api.get(`/documents/${doc.id}/extraction`);
            setCurrentExtraction(response.data);
            setDocumentForExtraction(doc);
            setExtractionDialogOpen(true);
        } catch (error) {
            console.error('Errore caricamento estrazione:', error);
            toast.error('Errore nel caricamento dei dati estratti');
        }
    };

    const handleValidateExtraction = async (validationData) => {
        try {
            const isIdDocument = currentExtraction?.is_id_document ||
                                 currentExtraction?.document_category === 'id_document';

            if (isIdDocument && validationData.field_selections) {
                await api.post(`/extractions/${validationData.extraction_id}/validate-merge`, validationData);
            } else {
                await api.post(`/extractions/${validationData.extraction_id}/validate`, validationData);
            }

            toast.success('Dati validati e applicati con successo');
            setExtractionDialogOpen(false);
            setCurrentExtraction(null);
            if (onRefresh) onRefresh();
        } catch (error) {
            console.error('Errore validazione estrazione:', error);
            throw error;
        }
    };

    const handleRegeneratePdf = async (doc) => {
        try {
            toast.info('Generazione PDF compilato...', { duration: 2000 });
            const response = await api.post(`/documents/${doc.id}/regenerate-pdf`);
            toast.success('PDF compilato generato con successo');

            if (response.data.download_url) {
                window.open(response.data.download_url, '_blank');
            }

            if (onRefresh) onRefresh();
        } catch (error) {
            console.error('Errore rigenerazione:', error);
            toast.error(error.response?.data?.detail || 'Errore durante la generazione');
        }
    };

    // Pulizia URL anteprima
    useEffect(() => {
        return () => {
            if (previewUrl) {
                window.URL.revokeObjectURL(previewUrl);
            }
        };
    }, [previewUrl]);

    const cardHandlers = {
        onUpload: handleUploadClick,
        onDownload: handleDownload,
        onPreview: handlePreview,
        onReplace: handleReplaceClick,
        onDelete: handleDeleteClick,
        onValidate: handleValidate,
        onViewVersions: handleViewVersions,
        onExtract: handleExtract,
        onVerifyExtraction: handleVerifyExtraction,
        onRegeneratePdf: handleRegeneratePdf
    };

    return (
        <div className="space-y-6">
            {/* Documenti obbligatori */}
            {requiredDocs.length > 0 && (
                <div>
                    <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                        <Warning weight="fill" className="w-4 h-4 text-red-500" />
                        Documenti Obbligatori
                    </h4>
                    <div className="space-y-3">
                        {requiredDocs.map((docReq, i) => (
                            <RequiredDocumentCard
                                key={docReq.code || `req-${i}`}
                                docReq={{...docReq, mandatory: true}}
                                uploadedDocs={docsByType[docReq.code] || []}
                                canValidate={canValidate}
                                nodeStatus={nodeStatus}
                                {...cardHandlers}
                            />
                        ))}
                    </div>
                </div>
            )}

            {/* Documenti facoltativi */}
            {optionalDocs.length > 0 && (
                <div>
                    <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                        <File className="w-4 h-4 text-muted-foreground" />
                        Documenti Facoltativi
                    </h4>
                    <div className="space-y-3">
                        {optionalDocs.map((docReq, i) => (
                            <RequiredDocumentCard
                                key={docReq.code || `opt-${i}`}
                                docReq={{...docReq, mandatory: false}}
                                uploadedDocs={docsByType[docReq.code] || []}
                                canValidate={canValidate}
                                nodeStatus={nodeStatus}
                                {...cardHandlers}
                            />
                        ))}
                    </div>
                </div>
            )}

            {/* Altri documenti */}
            {otherDocs.length > 0 && (
                <div>
                    <h4 className="text-sm font-semibold mb-3">Altri Documenti</h4>
                    <div className="space-y-2">
                        {otherDocs.map(doc => (
                            <DocumentRow
                                key={doc.id}
                                doc={doc}
                                onDownload={handleDownload}
                                onPreview={handlePreview}
                                onReplace={handleReplaceClick}
                                onDelete={handleDeleteClick}
                                onValidate={handleValidate}
                                onViewVersions={handleViewVersions}
                                canValidate={canValidate}
                                canDelete={nodeStatus === 'in_corso'}
                            />
                        ))}
                    </div>
                </div>
            )}

            {/* Carica altro documento */}
            {nodeStatus === 'in_corso' && (
                <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => {
                        setSelectedDocType({ code: 'ALTRO', label: 'Altro Documento' });
                        setReplaceDocId(null);
                        setUploadDialogOpen(true);
                    }}
                    data-testid="upload-other-document"
                >
                    <Upload className="w-4 h-4 mr-2" />
                    Carica Altro Documento
                </Button>
            )}

            {/* Stato vuoto */}
            {requiredDocs.length === 0 && optionalDocs.length === 0 && otherDocs.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                    <File className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>Nessun documento configurato per questo step</p>
                    {nodeStatus === 'in_corso' && (
                        <Button
                            variant="outline"
                            className="mt-4"
                            onClick={() => {
                                setSelectedDocType({ code: 'ALTRO', label: 'Documento' });
                                setUploadDialogOpen(true);
                            }}
                        >
                            <Upload className="w-4 h-4 mr-2" />
                            Carica Documento
                        </Button>
                    )}
                </div>
            )}

            <UploadDocumentDialog
                open={uploadDialogOpen}
                onOpenChange={setUploadDialogOpen}
                isReplace={!!replaceDocId}
                docTypeLabel={selectedDocType?.label}
                uploading={uploading}
                uploadProgress={uploadProgress}
                onUpload={handleUpload}
            />

            <PreviewDocumentDialog
                open={previewDialogOpen}
                onOpenChange={setPreviewDialogOpen}
                doc={previewDoc}
                previewUrl={previewUrl}
                onDownload={handleDownload}
            />

            <VersionsDialog
                open={versionsDialogOpen}
                onOpenChange={setVersionsDialogOpen}
                versions={selectedDocVersions}
                onDownload={handleDownload}
            />

            <DeleteDocumentDialog
                open={deleteDialogOpen}
                onOpenChange={setDeleteDialogOpen}
                doc={docToDelete}
                onConfirm={handleDelete}
            />

            {/* Dialog verifica estrazione - dinamico per categoria documento */}
            {currentExtraction?.is_id_document || currentExtraction?.document_category === 'id_document' ? (
                <IdDocumentVerificationDialog
                    isOpen={extractionDialogOpen}
                    onClose={() => {
                        setExtractionDialogOpen(false);
                        setCurrentExtraction(null);
                        setDocumentForExtraction(null);
                    }}
                    extraction={currentExtraction}
                    onValidate={handleValidateExtraction}
                    documentPreviewUrl={documentForExtraction?.file_path}
                />
            ) : (
                <ExtractionVerificationDialog
                    isOpen={extractionDialogOpen}
                    onClose={() => {
                        setExtractionDialogOpen(false);
                        setCurrentExtraction(null);
                        setDocumentForExtraction(null);
                    }}
                    extraction={currentExtraction}
                    onValidate={handleValidateExtraction}
                    documentPreviewUrl={documentForExtraction?.file_path}
                />
            )}
        </div>
    );
}
